import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import {
  Radar, RadarChart, PolarGrid, PolarAngleAxis, ResponsiveContainer,
  LineChart, Line, Tooltip, YAxis,
} from "recharts";
import { fetchArtist, fetchArtistHistory } from "@/lib/api";
import { RankBadge } from "@/components/RankBadge";
import { ShareButtons } from "@/components/ShareButtons";
import { AdSlot } from "@/components/AdSlot";
import { Seo } from "@/components/Seo";

const REGIONS = [
  { key: "us", label: "US" },
  { key: "gb", label: "UK" },
  { key: "eu", label: "Europe" },
  { key: "kr", label: "K-Pop" },
  { key: "ca", label: "Canada" },
  { key: "africa", label: "Afrobeats" },
];
const PILLARS = [
  { key: "charts", label: "Charts" },
  { key: "press", label: "Press Buzz" },
  { key: "fans", label: "Fanbase" },
];

const fmt = (n) => (n >= 1e6 ? `${(n / 1e6).toFixed(1)}M` : n >= 1e3 ? `${Math.round(n / 1e3)}K` : String(n || 0));

function ScoreTrend({ history }) {
  const data = history.map((h) => ({
    date: new Date(h.date).toLocaleDateString("en-US", { month: "short", day: "numeric" }),
    score: h.overall,
    rank: h.rank,
  }));
  const first = data[0]?.score ?? 0;
  const last = data[data.length - 1]?.score ?? 0;
  const delta = Math.round((last - first) * 10) / 10;
  return (
    <div className="border border-white/10 bg-card px-5 py-4 flex-1 min-w-[220px]" data-testid="score-trend">
      <div className="flex items-baseline justify-between">
        <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Score trend</span>
        <span className={`font-mono text-[11px] tabular-nums ${delta > 0 ? "text-emerald-400" : delta < 0 ? "text-signal" : "text-muted-foreground"}`} data-testid="score-trend-delta">
          {data.length < 2 ? `${data.length} sync` : `${delta > 0 ? "+" : ""}${delta} over ${data.length} syncs`}
        </span>
      </div>
      {data.length < 2 ? (
        <p className="font-mono text-[11px] text-muted-foreground mt-3">Trend appears after the next monthly sync.</p>
      ) : (
        <div className="h-14 mt-2">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data} margin={{ top: 4, right: 4, bottom: 0, left: 4 }}>
              <YAxis domain={["dataMin - 5", "dataMax + 5"]} hide />
              <Tooltip
                contentStyle={{ background: "#0a0a0a", border: "1px solid #27272a", fontFamily: "JetBrains Mono", fontSize: 11 }}
                labelStyle={{ color: "#a1a1aa" }}
                formatter={(v, _n, p) => [`${v}${p.payload.rank ? ` · #${p.payload.rank}` : ""}`, "Score"]}
              />
              <Line type="monotone" dataKey="score" stroke="#FF2E2E" strokeWidth={2} dot={{ r: 2.5, fill: "#FF2E2E", strokeWidth: 0 }} isAnimationActive={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

export default function ArtistProfile() {
  const { slug } = useParams();
  const [artist, setArtist] = useState(null);
  const [history, setHistory] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setArtist(null);
    setHistory([]);
    fetchArtist(slug).then(setArtist).catch(() => setNotFound(true));
    fetchArtistHistory(slug).then(setHistory).catch(() => {});
  }, [slug]);

  if (notFound) {
    return (
      <div className="text-center py-32">
        <Seo title="Artist not found" path={`/artist/${slug}`} />
        <h1 className="font-anton text-5xl uppercase">404</h1>
        <Link to="/rankings" className="inline-block mt-6 font-mono text-xs uppercase tracking-widest text-signal">
          ← Back to rankings
        </Link>
      </div>
    );
  }
  if (!artist) return <p className="font-mono text-sm text-muted-foreground text-center py-32">Loading…</p>;

  const scores = artist.scores || {};
  const regions = artist.regions || {};
  const radarData = REGIONS.map((r) => ({ stat: r.label, value: regions[r.key] ?? 0 }));
  const entries = artist.chart_entries || [];
  const pillarNotes = {
    charts: entries.length
      ? `Charting in ${[...new Set(entries.map((e) => e.region))].join(", ")} — peak #${Math.min(...entries.map((e) => e.position))} with "${artist.top_song}".`
      : "Not on the Apple Music Most Played charts this month.",
    press: artist.press_hits || artist.press_mentions
      ? `${artist.press_hits} headline${artist.press_hits === 1 ? "" : "s"} and ${artist.press_mentions}${artist.press_mentions >= 100 ? "+" : ""} mentions in the last 30 days across XXL, The Source, Billboard, Ebony, Essence, Complex & more.`
      : "No major press coverage tracked in the last 30 days.",
    fans: artist.fans_count ? `${fmt(artist.fans_count)} fans on Deezer.` : "Fanbase data unavailable.",
  };

  return (
    <div>
      <Seo title={`${artist.name} — Power Index`} description={artist.bio} image={artist.image} path={`/artist/${artist.slug}`} type="profile" />
      <div className="relative grain h-[55vh] min-h-[400px] border-b border-white/10 overflow-hidden">
        <img src={artist.image} alt={artist.name} className="w-full h-full object-cover img-treatment" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-black/20" />
        <div className="absolute inset-0 max-w-[1400px] mx-auto px-5 lg:px-10 flex flex-col justify-end pb-12">
          <span className="font-mono text-xs uppercase tracking-[0.3em] text-signal mb-3">
            Rank #{artist.rank ?? "—"} · {artist.title}
          </span>
          <h1 className="font-anton text-6xl md:text-8xl uppercase tracking-tight leading-none" data-testid="artist-name">
            {artist.name}
          </h1>
          <p className="font-mono text-sm uppercase tracking-widest text-muted-foreground mt-3">{artist.city}</p>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-14">
        <Link to="/rankings" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-muted-foreground hover:text-signal mb-10">
          <ArrowLeft className="w-4 h-4" /> All rankings
        </Link>

        <ShareButtons title={`${artist.name} — NERO SUN Power Index`} className="mb-8" />

        <div className="grid lg:grid-cols-[1.3fr_1fr] gap-10 items-start">
          <div>
            <div className="flex flex-wrap items-stretch gap-6 mb-8">
              <div className="border border-white/10 bg-card px-6 py-4">
                <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Overall</div>
                <div className="font-mono-stat text-5xl text-signal tabular-nums leading-none mt-1" data-testid="artist-overall">
                  {artist.overall}
                </div>
              </div>
              <div className="border border-white/10 bg-card px-6 py-4">
                <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Movement</div>
                <div className="mt-2"><RankBadge movement={artist.movement} className="text-base" /></div>
              </div>
              <ScoreTrend history={history} />
            </div>
            <p className="text-foreground/85 text-lg leading-relaxed">{artist.bio}</p>

            <div className="mt-10 space-y-4">
              {PILLARS.map((p) => (
                <div key={p.key} data-testid={`score-bar-${p.key}`}>
                  <div className="flex justify-between font-mono text-[11px] uppercase tracking-widest mb-1">
                    <span className="text-muted-foreground">{p.label}</span>
                    <span className="text-foreground">{scores[p.key] ?? 0}</span>
                  </div>
                  <div className="h-2 bg-secondary">
                    <div className="h-full bg-signal" style={{ width: `${scores[p.key] ?? 0}%` }} />
                  </div>
                </div>
              ))}
            </div>

            {entries.length > 0 && (
              <div className="mt-10 border border-white/10 divide-y divide-white/10" data-testid="chart-entries">
                <div className="px-4 py-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Chart positions this month</div>
                {entries.map((e, i) => (
                  <div key={i} className="flex items-center gap-4 px-4 py-3">
                    <span className="font-mono-stat text-xl text-signal tabular-nums w-12">#{e.position}</span>
                    <span className="font-display flex-1 truncate">{e.song}</span>
                    <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{e.region}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="border border-white/10 bg-card p-6">
            <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-4">Global Reach</div>
            <ResponsiveContainer width="100%" height={320}>
              <RadarChart data={radarData} outerRadius="75%">
                <PolarGrid stroke="#27272a" />
                <PolarAngleAxis dataKey="stat" tick={{ fill: "#a1a1aa", fontSize: 11, fontFamily: "JetBrains Mono" }} />
                <Radar dataKey="value" stroke="#FF2E2E" fill="#FF2E2E" fillOpacity={0.4} />
              </RadarChart>
            </ResponsiveContainer>
            <AdSlot variant="rectangle" slot={process.env.REACT_APP_ADSENSE_SLOT_ARTIST} className="mt-6" />
          </div>
        </div>

        <div className="grid sm:grid-cols-3 gap-px bg-white/10 border border-white/10 mt-14">
          {PILLARS.map((p) => (
            <div key={p.key} className="bg-card p-6" data-testid={`pillar-${p.key}`}>
              <div className="flex items-baseline justify-between">
                <span className="font-anton text-xl uppercase tracking-tight">{p.label}</span>
                <span className="font-mono-stat text-2xl text-signal tabular-nums">{scores[p.key] ?? 0}</span>
              </div>
              <p className="text-muted-foreground text-sm mt-2 leading-relaxed">{pillarNotes[p.key]}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
